import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';
import { useMcpConnection, useUpdateMcpConnection } from '@/lib/api/mcp-connections';
import { ConnectionForm } from '@/routes/mcp/connection-form';

export function McpConnectionEditPage({ connectionId }: { connectionId: string }) {
  const connectionQuery = useMcpConnection(connectionId);
  const updateConnection = useUpdateMcpConnection(connectionId);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (connectionQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (connectionQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load connection: {connectionQuery.error.message}
      </p>
    );
  }

  const connection = connectionQuery.data;
  const backToList = () => void navigate({ to: '/mcp/connections' });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Edit MCP connection</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">{connection.name}</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/mcp/connections">Back</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>The id is immutable — it's the ref prefix.</CardDescription>
        </CardHeader>
        <CardContent>
          <ConnectionForm
            initial={{
              id: connection.id,
              name: connection.name,
              url: connection.url,
              enabled: connection.enabled,
              maxConcurrentCalls: connection.maxConcurrentCalls,
            }}
            bearerTokenSet={connection.bearerTokenSet}
            submitting={updateConnection.isPending}
            submitLabel="Save connection"
            error={error}
            onCancel={backToList}
            onSubmit={({ id: _id, ...input }) => {
              setError(null);
              updateConnection.mutate(input, {
                onSuccess: backToList,
                onError: (caught) =>
                  setError(
                    caught instanceof ApiError ? caught.message : 'Could not save the connection.',
                  ),
              });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
